'use client';
import { RefObject, type ComponentPropsWithoutRef } from 'react';
import { Controller, useFormContext, type FieldPath, type FieldValues } from 'react-hook-form';

import { cn } from '@/slw/shared/lib/utils';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { Input as BaseInput } from '@/slw/shared/ui/input';

// ----------------------------------------------------------------------

interface CustomRHFInputProps<TFieldValues extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<ComponentPropsWithoutRef<typeof BaseInput>, 'name' | 'defaultValue'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  label?: string;
  withError?: boolean;
  showMessError?: boolean;
  direction?: 'row' | 'column';
  labelClass?: string;
  required?: boolean;
  onlyNumber?: boolean;
  onlyNumberFormat?: boolean;
  ref?: RefObject<HTMLInputElement | null>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const formatNumberInput = (val: string | number) => {
  const numeric = `${val}`.replace(/[^\d]/g, '');

  if (!numeric) return '';

  return parseInt(numeric, 10).toLocaleString('en-US');
};

export const formatSaveNumber = (val?: string | number): number => {
  if (!val) return 0;

  const numeric = `${val}`.replace(/[^\d]/g, '');

  if (!numeric) return 0;

  return parseInt(numeric, 10);
};

export const formatOnlyNumber = (val?: string | number): number | string => {
  if (val === undefined) return '';

  const numeric = `${val}`.replace(/[^\d]/g, '');

  if (numeric || numeric === '0') return parseInt(numeric, 10);

  return '';
};

export const Input = ({
  name,
  label,
  helperText,
  type = 'text',
  defaultValue,
  containerClass,
  labelClass,
  className,
  withError = true,
  showMessError = true,
  direction = 'row',
  required,
  onlyNumber,
  onlyNumberFormat,
  onKeyDown,
  ...other
}: CustomRHFInputProps<any>) => {
  const { control, setValue } = useFormContext();
  const directionClass = direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'flex flex-col items-start';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'number') {
      if (!/[0-9]|Backspace|ArrowLeft|ArrowRight|Delete|Tab/.test(e.key)) {
        e.preventDefault();
      }

      onKeyDown?.(e);
    } else {
      onKeyDown?.(e);
    }
  };

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className={cn(directionClass, containerClass)}>
          {!!label && (
            <FormLabel className={cn('whitespace-nowrap font-normal text-sm leading-[1.5]', labelClass)}>
              {label}
              {required && <span className='text-[#dc2626]'>*</span>}
            </FormLabel>
          )}
          <FormControl>
            <BaseInput
              {...field}
              {...other}
              size='lg'
              type={type}
              onKeyDown={handleKeyDown}
              onChange={(event) => {
                if (type === 'number') {
                  const raw = event.currentTarget.value;

                  // Allow clearing the field without forcing it to 0
                  if (raw === '') {
                    setValue(name as string, '' as any, { shouldValidate: true });
                  } else {
                    setValue(name as string, Number(raw), { shouldValidate: true });
                  }
                } else {
                  if (onlyNumber) {
                    const formatted = formatOnlyNumber(event.currentTarget.value);

                    setValue(name as string, `${formatted}`, { shouldValidate: true });

                    return;
                  }

                  if (onlyNumberFormat) {
                    const formatted = formatNumberInput(event.currentTarget.value);

                    setValue(name as string, formatted, { shouldValidate: true });

                    return;
                  }

                  field.onChange(event);
                }
              }}
              className={cn(className, error && withError && 'border-destructive focus-visible:ring-destructive')}
            />
          </FormControl>
          {helperText && !error && withError && showMessError && (
            <p className='text-xs leading-[140%] text-muted-foreground'>{helperText}</p>
          )}
          {error && withError && showMessError && (
            <FormMessage className='py-0 text-xs leading-[140%]'>{error.message}</FormMessage>
          )}
        </FormItem>
      )}
    />
  );
};
